import { useState } from 'react'
import { Youtube, Globe, StickyNote, Image as ImageIcon, Clock, Check, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

// Utility for class names
function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

// Web globe icon using worldwide.png
function WebGlobeIcon({ className }: { className?: string }) {
  return <img src="/worldwide.png" alt="Web" className={cn('object-contain', className)} />
}

interface LearningPath {
  id: string
  title: string
  status: string
}

interface ContentCardProps {
  id: string
  title: string
  summary?: string
  url?: string
  thumbnail?: string
  createdAt?: string
  category?: string
  duration?: string | number
  metadata?: any
  tags?: string[]
  isSelected?: boolean
  selectionMode?: boolean
  highlightId?: string | null
  learningPaths?: LearningPath[]
  onSelect?: () => void
  onClick?: () => void
}

// Safely format date with fallback
function formatDate(dateStr?: string): string {
  if (!dateStr) return ''
  
  try {
    const date = new Date(dateStr)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return ''
    }
    
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) {
      // Today - show time
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    } else if (diffDays < 365) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  } catch {
    return ''
  }
}

// Format duration (for YouTube videos)
function formatDuration(duration?: string | number): string {
  if (!duration) return ''
  
  if (typeof duration === 'number') {
    const hours = Math.floor(duration / 3600)
    const minutes = Math.floor((duration % 3600) / 60)
    const seconds = Math.floor(duration % 60)
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }
  
  // ISO 8601 format
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
  if (!match) return String(duration)
  
  const hours = parseInt(match[1] || '0')
  const minutes = parseInt(match[2] || '0')
  const seconds = parseInt(match[3] || '0')
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// Detect content type
function getContentType(url?: string, metadata?: any, tags?: string[], title?: string): 'youtube' | 'screenshot' | 'note' | 'web' {
  // Check for note
  if (metadata?.type === 'note' || tags?.includes('note') || title?.startsWith('Note:')) {
    return 'note'
  }
  
  // Check for screenshot
  if (metadata?.type === 'screenshot' || tags?.includes('screenshot') || title?.includes('Screenshot')) {
    return 'screenshot'
  }
  
  // Check for YouTube
  if (url && (url.includes('youtube.com') || url.includes('youtu.be'))) {
    return 'youtube'
  }
  
  return 'web'
}

// Get type config
function getTypeConfig(type: 'youtube' | 'screenshot' | 'note' | 'web') {
  switch (type) {
    case 'youtube':
      return { 
        Icon: Youtube, 
        label: 'YouTube', 
        iconClass: 'text-red-500',
        bgClass: 'bg-red-500/10'
      }
    case 'screenshot':
      return { 
        Icon: ImageIcon, 
        label: 'Screenshot', 
        iconClass: 'text-violet-500',
        bgClass: 'bg-violet-500/10'
      }
    case 'note':
      return { 
        Icon: StickyNote, 
        label: 'Note', 
        iconClass: 'text-amber-500',
        bgClass: 'bg-amber-500/10'
      }
    default:
      return { 
        Icon: WebGlobeIcon, 
        label: 'Web', 
        iconClass: 'text-blue-500',
        bgClass: 'bg-blue-500/10'
      }
  }
}

// Extract YouTube ID
function extractYouTubeId(url?: string): string | null {
  if (!url) return null
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
  return match ? match[1] : null
}

// Extract clean domain/source name from URL
function getSourceName(url?: string, contentType?: string): string | null {
  // Don't show source for notes/screenshots
  if (contentType === 'note' || contentType === 'screenshot') return null
  if (!url) return null
  
  try {
    const hostname = new URL(url).hostname.replace('www.', '')
    
    // Map common domains to friendly names
    const domainMap: Record<string, string> = {
      'youtube.com': 'YouTube',
      'youtu.be': 'YouTube',
      'medium.com': 'Medium',
      'dev.to': 'Dev.to',
      'github.com': 'GitHub',
      'stackoverflow.com': 'Stack Overflow',
      'twitter.com': 'Twitter',
      'x.com': 'X',
      'linkedin.com': 'LinkedIn',
      'notion.so': 'Notion',
      'substack.com': 'Substack',
      'reddit.com': 'Reddit',
      'hackernews.com': 'Hacker News',
      'news.ycombinator.com': 'Hacker News',
      'arxiv.org': 'arXiv',
      'docs.google.com': 'Google Docs',
      'drive.google.com': 'Google Drive',
      'figma.com': 'Figma',
      'vercel.com': 'Vercel',
      'netlify.com': 'Netlify',
      'aws.amazon.com': 'AWS',
      'cloud.google.com': 'Google Cloud',
      'azure.microsoft.com': 'Azure',
      'openai.com': 'OpenAI',
      'anthropic.com': 'Anthropic',
      'huggingface.co': 'Hugging Face',
    }
    
    // Check for exact match first
    if (domainMap[hostname]) {
      return domainMap[hostname]
    }
    
    // Check for subdomain matches (e.g., blog.example.com)
    for (const [domain, name] of Object.entries(domainMap)) {
      if (hostname.endsWith('.' + domain) || hostname === domain) {
        return name
      }
    }
    
    // Fallback: capitalize first part of domain
    const parts = hostname.split('.')
    const mainPart = parts.length > 2 ? parts[parts.length - 2] : parts[0]
    return mainPart.charAt(0).toUpperCase() + mainPart.slice(1)
  } catch {
    return null
  }
}

export default function ContentCard({
  id,
  title,
  summary,
  url,
  thumbnail,
  createdAt,
  category,
  duration,
  metadata,
  tags,
  isSelected = false,
  selectionMode = false,
  highlightId,
  learningPaths = [],
  onSelect,
  onClick
}: ContentCardProps) {
  const [imgError, setImgError] = useState(false)
  const navigate = useNavigate()
  
  const contentType = getContentType(url, metadata, tags, title)
  const typeConfig = getTypeConfig(contentType)
  const TypeIcon = typeConfig.Icon
  
  // Get the actual thumbnail
  let actualThumbnail = thumbnail || metadata?.thumbnail || metadata?.image_url || metadata?.image
  
  // For YouTube, generate thumbnail if not provided
  if (contentType === 'youtube' && !actualThumbnail && url) {
    const videoId = extractYouTubeId(url)
    if (videoId) {
      actualThumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    }
  }
  
  // For screenshots, use the image_url
  if (contentType === 'screenshot' && metadata?.image_url) {
    actualThumbnail = metadata.image_url
  }
  
  const formattedDate = formatDate(createdAt || metadata?.created_at || metadata?.display_date)
  const formattedDuration = formatDuration(duration || metadata?.duration)
  const sourceName = getSourceName(url, contentType)
  
  const displayTitle = title?.replace('Note: ', '') || 'Untitled'
  const displaySummary = summary || metadata?.content || ''
  
  const handleClick = () => {
    if (selectionMode && onSelect) {
      onSelect()
    } else if (onClick) {
      onClick()
    }
  }

  // Show actual image for all content types that have a thumbnail
  const showImage = !!(actualThumbnail && !imgError)
  
  return (
    <div
      id={`content-item-${id}`}
      className={cn(
        "relative rounded-xl overflow-hidden cursor-pointer group memtrail-card",
        "border-2 border-gray-200 dark:border-gray-700",
        "bg-white dark:bg-gray-800",
        "hover:border-gray-400 dark:hover:border-gray-500",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400",
        isSelected && "ring-2 ring-blue-500",
        highlightId === id && "ring-2 ring-blue-500 shadow-lg",
        // Fixed height for grid view to ensure consistency
        "h-[220px]"
      )}
      onClick={handleClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      {/* Selection Indicator */}
      {selectionMode && (
        <div className="absolute top-2.5 left-2.5 z-20">
          <div className={cn(
            "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
            isSelected 
              ? "bg-blue-500 border-blue-500" 
              : "border-gray-400 bg-white/90 backdrop-blur-sm"
          )}>
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </div>
        </div>
      )}

      {/* Card Content */}
      <div className="h-full flex flex-col">
        {/* Thumbnail / Pattern Area */}
        <div className="relative overflow-hidden flex-shrink-0 h-28">
          {showImage ? (
            // Actual thumbnail/screenshot
            <img 
              src={actualThumbnail}
              alt={displayTitle}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            // Grid pattern fallback - NO big icon, just the pattern
            <div 
              className={cn(
                "w-full h-full",
                "bg-gradient-to-br from-gray-50 to-gray-100",
                "dark:from-gray-800 dark:to-gray-900",
                "bg-grid-pattern-light dark:bg-grid-pattern",
                "bg-repeat bg-[length:30px_30px]"
              )}
            >
              {/* Gradient overlay - clean, no icon */}
              <div className={cn(
                "w-full h-full",
                "bg-gradient-to-tr from-white/90 via-white/50 to-white/20",
                "dark:from-gray-900/90 dark:via-gray-900/50 dark:to-gray-900/20"
              )} />
            </div>
          )}
          
          {/* Type Badge - top left */}
          <div className={cn(
            "absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-md",
            "bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm",
            "border border-gray-200/50 dark:border-gray-700/50",
            "shadow-sm"
          )}>
            <TypeIcon className={cn("w-3 h-3", typeConfig.iconClass)} />
            <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">
              {typeConfig.label}
            </span>
          </div>
          
          {/* Source Badge - top right */}
          {sourceName && (
            <div className={cn(
              "absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md",
              "bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm",
              "border border-gray-200/50 dark:border-gray-700/50",
              "shadow-sm",
              "max-w-[120px]"
            )}>
              <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 truncate">
                {sourceName}
              </span>
              <ExternalLink className="w-2.5 h-2.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
          )}
          
          {/* Duration Badge - bottom right (for YouTube) */}
          {formattedDuration && (
            <div className="absolute bottom-2 right-2">
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-black/80 backdrop-blur-sm rounded text-[10px] font-medium text-white">
                <Clock className="w-2.5 h-2.5" />
                {formattedDuration}
              </div>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="relative flex-1 flex flex-col p-3">
          {/* Title */}
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2 mb-2">
            {displayTitle}
          </h3>
          
          {/* Spacer to push meta row to bottom */}
          <div className="flex-1" />
          
          {/* Meta Row - Positioned at bottom with consistent spacing */}
          <div className="flex items-center gap-2 pt-2">
            {/* Category */}
            {category && (
              <span className={cn(
                "px-2 py-0.5 rounded text-[10px] font-medium",
                "bg-gray-100 dark:bg-gray-700",
                "text-gray-600 dark:text-gray-300",
                "border border-gray-200 dark:border-gray-600"
              )}>
                {category}
              </span>
            )}
            
            {/* Learning Path Badge */}
            {learningPaths && learningPaths.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/learning-paths/${learningPaths[0].id}`)
                }}
                className={cn(
                  "px-2 py-0.5 rounded text-[10px] font-semibold",
                  "bg-blue-50 dark:bg-blue-900/30",
                  "text-blue-700 dark:text-blue-400",
                  "border border-blue-200 dark:border-blue-800",
                  "hover:bg-blue-100 dark:hover:bg-blue-900/50",
                  "transition-colors duration-200",
                  "flex items-center gap-1"
                )}
                title={learningPaths.length === 1 
                  ? `In trail: ${learningPaths[0].title}` 
                  : `In ${learningPaths.length} trails`}
              >
                <span>🎯</span>
                {learningPaths.length === 1 ? 'In Trail' : `In ${learningPaths.length} Trails`}
              </button>
            )}
          </div>
          
          {/* Date - Absolute positioned in bottom-right corner */}
          {formattedDate && (
            <div className="absolute bottom-3 right-3">
              <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                {formattedDate}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Export a minimal card for use inside 3D folder previews
export function ContentCardMini({
  title,
  type,
  thumbnail
}: {
  title: string
  type?: string
  thumbnail?: string
}) {
  const contentType = (type as 'youtube' | 'screenshot' | 'note' | 'web') || 'web'
  const typeConfig = getTypeConfig(contentType)
  const TypeIcon = typeConfig.Icon
  
  return (
    <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
      {thumbnail ? (
        <img src={thumbnail} alt="" className="w-8 h-8 rounded object-cover" />
      ) : (
        <div className={cn("w-8 h-8 rounded flex items-center justify-center", typeConfig.bgClass)}>
          <TypeIcon className={cn("w-4 h-4", typeConfig.iconClass)} />
        </div>
      )}
      <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">
        {title?.replace('Note: ', '').slice(0, 25)}...
      </span>
    </div>
  )
}
